import React, { useEffect, useState } from 'react';
import { useWedding } from '../contexts/WeddingContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectSeparator,
} from '../components/ui/select';
import {
  Plus, Edit, Trash, Calendar as CalendarIcon, WarningTriangle,
  NavArrowDown, NavArrowUp, Xmark, Bell,
} from 'iconoir-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NEW_VENDOR_SENTINEL = '__new_vendor__';

const EMPTY_INSTALLMENT = () => ({
  // _key is a stable client-side id used as React's `key` so adding/removing
  // installments doesn't cause input focus to jump or stale state to leak
  // between rows. Never sent to the backend (stripped before POST/PUT below).
  _key: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `inst-${Date.now()}-${Math.random()}`,
  amount: '', due_date: '', paid: false, paid_date: null,
  responsible_family: '', paid_by_family: '',
});
const EMPTY_FORM = () => ({
  description: '',
  vendor_id: '',
  responsible_family: '',
  installments: [EMPTY_INSTALLMENT()],
});

// Visual chip for a family attribution. Hidden when key is falsy.
const FAMILY_LABELS = { partner1: 'P1', partner2: 'P2', joint: 'Joint' };
const FAMILY_BG = {
  partner1: 'bg-terracotta/10 text-terracotta border-terracotta/30',
  partner2: 'bg-moss/10 text-moss border-moss/30',
  joint:    'bg-ochre/10 text-ochre border-ochre/30',
};
function FamilyTag({ value, partnerNames, prefix = '', testId }) {
  if (!value) return null;
  const label = value === 'joint'
    ? 'Joint'
    : (partnerNames?.[value] || FAMILY_LABELS[value]);
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-wider ${FAMILY_BG[value]}`}
      data-testid={testId}
    >
      {prefix}{label}
    </span>
  );
}

export default function PaymentsPage({ bare = false }) {
  const { currentWedding, vendors, categories, fetchVendors, createVendor } = useWedding();
  const [payments, setPayments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM());
  const [newVendor, setNewVendor] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [emailReminders, setEmailReminders] = useState(false);
  const [weeklyDigest, setWeeklyDigest] = useState(false);

  const fetchPayments = async () => {
    if (!currentWedding) return;
    try {
      const { data } = await axios.get(`${API}/payment-schedule/${currentWedding.id}`, { withCredentials: true });
      setPayments(data);
    } catch (err) { console.error('Error fetching payments:', err); }
  };

  const fetchPreferences = async () => {
    try {
      const { data } = await axios.get(`${API}/me/preferences`, { withCredentials: true });
      setEmailReminders(!!data?.email_reminders);
      setWeeklyDigest(!!data?.weekly_digest);
    } catch { /* silent — fall back to default */ }
  };

  const togglePreference = async (key, next, label) => {
    try {
      await axios.put(`${API}/me/preferences`, { [key]: next }, { withCredentials: true });
      toast.success(next ? `${label} on` : `${label} off`);
      return true;
    } catch {
      toast.error('Could not update preference');
      return false;
    }
  };

  const toggleEmailReminders = async () => {
    const next = !emailReminders;
    setEmailReminders(next);
    const ok = await togglePreference('email_reminders', next,
      next ? "We'll email you 3 days before each installment is due." : 'Reminder emails');
    if (!ok) setEmailReminders(!next);
  };

  const toggleWeeklyDigest = async () => {
    const next = !weeklyDigest;
    setWeeklyDigest(next);
    const ok = await togglePreference('weekly_digest', next,
      next ? "You'll get your Sunday digest every week." : 'Weekly digest');
    if (!ok) setWeeklyDigest(!next);
  };

  useEffect(() => {
    fetchPayments();
    fetchPreferences();
    if (currentWedding) fetchVendors(currentWedding.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWedding]);

  const openModal = (payment = null) => {
    if (payment) {
      setEditingPayment(payment);
      setFormData({
        description: payment.description,
        vendor_id: payment.vendor_id || '',
        responsible_family: payment.responsible_family || '',
        installments: (payment.installments || []).map((i, idx) => ({
          _key: `existing-${payment.id}-${idx}`,
          amount: i.amount?.toString() || '',
          due_date: i.due_date || '',
          paid: !!i.paid,
          paid_date: i.paid_date || null,
          responsible_family: i.responsible_family || '',
          paid_by_family: i.paid_by_family || '',
        })),
      });
    } else {
      setEditingPayment(null);
      setFormData(EMPTY_FORM());
    }
    setNewVendor(null);
    setShowModal(true);
  };

  const handleVendorSelect = (value) => {
    if (value === NEW_VENDOR_SENTINEL) {
      setNewVendor({ name: '', category: '', phone: '', email: '' });
      setFormData((prev) => ({ ...prev, vendor_id: '' }));
    } else {
      setFormData((prev) => ({ ...prev, vendor_id: value }));
    }
  };

  const updateInstallment = (idx, patch) => {
    setFormData((prev) => ({
      ...prev,
      installments: prev.installments.map((i, n) => (n === idx ? { ...i, ...patch } : i)),
    }));
  };
  const addInstallment = () => setFormData((prev) => ({
    ...prev,
    installments: [...prev.installments, EMPTY_INSTALLMENT()],
  }));
  const removeInstallment = (idx) => setFormData((prev) => ({
    ...prev,
    installments: prev.installments.length <= 1
      ? prev.installments
      : prev.installments.filter((_, n) => n !== idx),
  }));

  // Quick presets for 2 or 3 evenly-split installments (user can edit any value after)
  const applyPreset = (count) => {
    const existingTotal = formData.installments.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    const firstDue = formData.installments[0]?.due_date || '';
    const slices = [];
    const share = existingTotal > 0 ? Math.floor((existingTotal / count) * 100) / 100 : '';
    for (let n = 0; n < count; n += 1) {
      slices.push({
        ...EMPTY_INSTALLMENT(),
        amount: share === '' ? '' : share.toString(),
        due_date: n === 0 ? firstDue : '',
      });
    }
    setFormData((prev) => ({ ...prev, installments: slices }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let vendorId = formData.vendor_id || null;

    if (newVendor && newVendor.name.trim()) {
      try {
        const created = await createVendor({
          wedding_id: currentWedding.id,
          name: newVendor.name.trim(),
          category: newVendor.category || 'Other',
          contact_name: '', phone: newVendor.phone || '', email: newVendor.email || '',
          website: '', instagram: '', facebook: '', tiktok: '', whatsapp: '',
          event_ids: [], notes: '',
        });
        vendorId = created?.id || null;
        toast.success(`Added ${newVendor.name.trim()} to your vendors`);
      } catch { toast.error('Could not create vendor — saving payment without link'); }
    }

    const cleanInstallments = formData.installments
      .map((i) => ({
        // strip the client-side _key — it's a React render aid, not data.
        amount: parseFloat(i.amount) || 0,
        due_date: i.due_date || '',
        paid: !!i.paid,
        paid_date: i.paid_date || null,
        responsible_family: i.responsible_family || null,
        paid_by_family: i.paid_by_family || null,
      }))
      .filter((i) => i.amount > 0 && i.due_date);

    if (cleanInstallments.length === 0) {
      toast.error('Add at least one installment with an amount and due date');
      return;
    }

    const payload = {
      wedding_id: currentWedding.id,
      description: formData.description,
      vendor_id: vendorId,
      responsible_family: formData.responsible_family || null,
      installments: cleanInstallments,
    };

    try {
      if (editingPayment) {
        await axios.put(`${API}/payment-schedule/${editingPayment.id}`, payload, { withCredentials: true });
        toast.success('Payment plan updated');
      } else {
        await axios.post(`${API}/payment-schedule`, payload, { withCredentials: true });
        toast.success(`Added with ${cleanInstallments.length} installment${cleanInstallments.length > 1 ? 's' : ''}`);
      }
      setShowModal(false);
      setNewVendor(null);
      fetchPayments();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (paymentId) => {
    if (window.confirm('Delete this entire payment plan (including all installments)?')) {
      await axios.delete(`${API}/payment-schedule/${paymentId}`, { withCredentials: true });
      fetchPayments();
    }
  };

  const handleToggleInstallment = async (payment, idx) => {
    // Flip the installment's paid flag and persist the whole list.
    const updated = payment.installments.map((inst, n) => (
      n === idx
        ? { ...inst, paid: !inst.paid, paid_date: !inst.paid ? new Date().toISOString().slice(0, 10) : null }
        : inst
    ));
    await axios.put(
      `${API}/payment-schedule/${payment.id}`,
      { installments: updated },
      { withCredentials: true },
    );
    fetchPayments();
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: currentWedding?.preferred_currency || 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(amount || 0);

  // Installment-level buckets for the summary cards
  const today = new Date().toISOString().slice(0, 10);
  const unpaidInstallments = [];
  payments.forEach((p) => (p.installments || []).forEach((i) => {
    if (!i.paid) unpaidInstallments.push({ ...i, payment_id: p.id, description: p.description });
  }));
  const overdueInst = unpaidInstallments.filter((i) => i.due_date && i.due_date < today);
  const upcomingInst = unpaidInstallments.filter((i) => i.due_date && i.due_date >= today);
  const totalDue = unpaidInstallments.reduce((s, i) => s + (i.amount || 0), 0);
  const totalOverdue = overdueInst.reduce((s, i) => s + (i.amount || 0), 0);

  const getVendorName = (vendorId) => vendors.find((v) => v.id === vendorId)?.name || '';

  if (!currentWedding) {
    const empty = <div className="text-center py-12 text-muted">Please select or create a wedding first.</div>;
    return bare ? empty : <Layout>{empty}</Layout>;
  }

  // Split payments into those with at least one overdue installment, all-paid, and rest
  const allPaid = payments.filter((p) => p.all_paid);
  const withOverdue = payments.filter((p) => !p.all_paid && (p.installments || []).some((i) => !i.paid && i.due_date && i.due_date < today));
  const active = payments.filter((p) => !p.all_paid && !withOverdue.includes(p));

  const renderPaymentCard = (payment, flaggedOverdue = false) => {
    const isOpen = expandedId === payment.id;
    const total = payment.total_amount || 0;
    const nextUnpaid = (payment.installments || []).find((i) => !i.paid && i.due_date);
    const partnerNames = {
      partner1: currentWedding?.partner1_name,
      partner2: currentWedding?.partner2_name,
    };
    return (
      <Card
        key={payment.id}
        className={`card-surface overflow-hidden ${flaggedOverdue ? 'border-terracotta/30' : ''}`}
        data-testid={`payment-card-${payment.id}`}
      >
        <button
          type="button"
          onClick={() => setExpandedId(isOpen ? null : payment.id)}
          className="w-full flex items-center gap-4 px-4 py-3 hover:bg-page/50 transition-colors text-left"
          data-testid={`payment-toggle-${payment.id}`}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`font-sans text-charcoal font-medium ${payment.all_paid ? 'line-through opacity-60' : ''}`}>
                {payment.description}
              </span>
              {flaggedOverdue && <WarningTriangle className="w-4 h-4 text-terracotta" />}
              {payment.vendor_id && (
                <span className="text-xs text-terracotta">{getVendorName(payment.vendor_id)}</span>
              )}
              <FamilyTag
                value={payment.responsible_family}
                partnerNames={partnerNames}
                prefix="Resp: "
                testId={`payment-family-${payment.id}`}
              />
            </div>
            <div className="flex items-center gap-3 text-xs text-muted mt-1 flex-wrap">
              <span className="font-mono">
                {payment.paid_count} of {payment.installment_count} paid
              </span>
              {nextUnpaid && (
                <span className={flaggedOverdue ? 'text-terracotta font-medium' : ''}>
                  Next: {formatCurrency(nextUnpaid.amount)} on{' '}
                  {new Date(`${nextUnpaid.due_date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              )}
            </div>
          </div>
          <span className={`font-mono text-sm ${flaggedOverdue ? 'text-terracotta' : 'text-charcoal'}`}>
            {formatCurrency(total)}
          </span>
          {isOpen ? <NavArrowUp className="w-4 h-4 text-muted" /> : <NavArrowDown className="w-4 h-4 text-muted" />}
        </button>

        {isOpen && (
          <div className="border-t border-border divide-y divide-border bg-page/20">
            {(payment.installments || []).map((inst, idx) => {
              const instOverdue = !inst.paid && inst.due_date && inst.due_date < today;
              return (
                <div
                  key={`${payment.id}-${idx}-${inst.due_date || ''}-${inst.amount || ''}`}
                  className={`flex items-center gap-3 px-4 py-2.5 ${instOverdue ? 'bg-terracotta/5' : ''}`}
                  data-testid={`installment-${payment.id}-${idx}`}
                >
                  <Checkbox
                    checked={inst.paid}
                    onCheckedChange={() => handleToggleInstallment(payment, idx)}
                    data-testid={`installment-toggle-${payment.id}-${idx}`}
                  />
                  <span className="text-xs font-mono text-muted w-8">#{idx + 1}</span>
                  <div className="flex-1 text-xs">
                    <span className={`${inst.paid ? 'line-through opacity-60' : ''} ${instOverdue ? 'text-terracotta font-medium' : 'text-charcoal'}`}>
                      Due {inst.due_date
                        ? new Date(`${inst.due_date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        : '—'}
                      {instOverdue && <span className="ml-2">(Overdue)</span>}
                      {inst.paid && inst.paid_date && (
                        <span className="ml-2 text-moss">Paid {inst.paid_date}</span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <FamilyTag
                      value={inst.responsible_family || payment.responsible_family}
                      partnerNames={partnerNames}
                      prefix="Resp: "
                      testId={`installment-resp-${payment.id}-${idx}`}
                    />
                    <FamilyTag
                      value={inst.paid_by_family || payment.paid_by_family}
                      partnerNames={partnerNames}
                      prefix="Paid: "
                      testId={`installment-paid-by-${payment.id}-${idx}`}
                    />
                  </div>
                  <span className={`font-mono text-sm ${inst.paid ? 'line-through opacity-60' : instOverdue ? 'text-terracotta' : 'text-charcoal'}`}>
                    {formatCurrency(inst.amount)}
                  </span>
                </div>
              );
            })}
            <div className="flex gap-2 px-4 py-2 justify-end">
              <button onClick={() => openModal(payment)} className="text-xs text-muted hover:text-charcoal transition-colors flex items-center gap-1" data-testid={`edit-payment-${payment.id}`}>
                <Edit className="w-3 h-3" /> Edit plan
              </button>
              <button onClick={() => handleDelete(payment.id)} className="text-xs text-muted hover:text-terracotta transition-colors flex items-center gap-1" data-testid={`delete-payment-${payment.id}`}>
                <Trash className="w-3 h-3" /> Delete
              </button>
            </div>
          </div>
        )}
      </Card>
    );
  };

  const installmentsFormSum = formData.installments.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

  const content = (
      <div className="space-y-8" data-testid="payments-page">
        {!bare && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="font-serif font-light text-4xl sm:text-5xl text-charcoal tracking-tight">Payments</h1>
              <p className="text-muted font-light mt-2">
                Each entry is one vendor bill, split across up to any number of installments (deposit → mid → final).
              </p>
            </div>
            <Button onClick={() => openModal()} className="btn-primary flex items-center gap-2" data-testid="add-payment-btn">
              <Plus className="w-4 h-4" /> Add Payment
            </Button>
          </div>
        )}
        {bare && (
          <div className="flex justify-end">
            <Button onClick={() => openModal()} className="btn-primary flex items-center gap-2" data-testid="add-payment-btn">
              <Plus className="w-4 h-4" /> Add Payment
            </Button>
          </div>
        )}

        {/* Email preferences — both persistent, quiet design */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex items-center justify-between px-4 py-3 border border-border bg-page/50 rounded-sm">
            <div className="flex items-center gap-2 min-w-0">
              <Bell className="w-4 h-4 text-muted shrink-0" strokeWidth={1.5} />
              <span className="text-sm text-charcoal truncate">
                Email me 3 days before each due date
              </span>
            </div>
            <label className="inline-flex items-center cursor-pointer shrink-0" data-testid="reminder-toggle-label">
              <input
                type="checkbox"
                checked={emailReminders}
                onChange={toggleEmailReminders}
                className="sr-only peer"
                data-testid="reminder-toggle-checkbox"
              />
              <div className="relative w-10 h-5 bg-border rounded-full peer-checked:bg-terracotta transition-colors">
                <div
                  className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${emailReminders ? 'translate-x-5' : ''}`}
                />
              </div>
            </label>
          </div>

          <div className="flex items-center justify-between px-4 py-3 border border-border bg-page/50 rounded-sm">
            <div className="flex items-center gap-2 min-w-0">
              <CalendarIcon className="w-4 h-4 text-muted shrink-0" strokeWidth={1.5} />
              <span className="text-sm text-charcoal truncate">
                Sunday digest — week ahead summary
              </span>
            </div>
            <label className="inline-flex items-center cursor-pointer shrink-0" data-testid="weekly-digest-toggle-label">
              <input
                type="checkbox"
                checked={weeklyDigest}
                onChange={toggleWeeklyDigest}
                className="sr-only peer"
                data-testid="weekly-digest-toggle-checkbox"
              />
              <div className="relative w-10 h-5 bg-border rounded-full peer-checked:bg-terracotta transition-colors">
                <div
                  className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${weeklyDigest ? 'translate-x-5' : ''}`}
                />
              </div>
            </label>
          </div>
        </div>

        {/* Summary — computed across installments */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="card-surface p-6">
            <p className="text-sm text-muted uppercase tracking-wider mb-1">Total Due</p>
            <p className="font-mono text-2xl text-charcoal tracking-tight" data-testid="summary-total-due">
              {formatCurrency(totalDue)}
            </p>
            <p className="text-xs text-muted mt-1">{unpaidInstallments.length} unpaid installment{unpaidInstallments.length === 1 ? '' : 's'}</p>
          </Card>
          <Card className={`card-surface p-6 ${totalOverdue > 0 ? 'border-terracotta/30' : ''}`}>
            <p className="text-sm text-muted uppercase tracking-wider mb-1">Overdue</p>
            <p className={`font-mono text-2xl tracking-tight ${totalOverdue > 0 ? 'text-terracotta' : 'text-charcoal'}`}
              data-testid="summary-overdue">
              {formatCurrency(totalOverdue)}
            </p>
            <p className="text-xs text-muted mt-1">{overdueInst.length} installment{overdueInst.length === 1 ? '' : 's'}</p>
          </Card>
          <Card className="card-surface p-6">
            <p className="text-sm text-muted uppercase tracking-wider mb-1">Upcoming</p>
            <p className="font-mono text-2xl text-charcoal tracking-tight" data-testid="summary-upcoming">
              {upcomingInst.length}
            </p>
            <p className="text-xs text-muted mt-1">installment{upcomingInst.length === 1 ? '' : 's'} scheduled</p>
          </Card>
        </div>

        {withOverdue.length > 0 && (
          <div className="space-y-3">
            <h2 className="font-serif text-lg text-terracotta flex items-center gap-2">
              <WarningTriangle className="w-5 h-5" /> Plans with overdue installments
            </h2>
            {withOverdue.map((p) => renderPaymentCard(p, true))}
          </div>
        )}

        <div className="space-y-3">
          <h2 className="font-serif text-lg text-charcoal">Active plans</h2>
          {active.length === 0 && !withOverdue.length ? (
            <Card className="card-surface p-8 text-center text-muted">No active payment plans yet</Card>
          ) : (
            active.map((p) => renderPaymentCard(p))
          )}
        </div>

        {allPaid.length > 0 && (
          <div className="space-y-3 opacity-75">
            <h2 className="font-serif text-lg text-muted">Completed ({allPaid.length})</h2>
            {allPaid.map((p) => renderPaymentCard(p))}
          </div>
        )}

        {/* Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-white max-w-xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">{editingPayment ? 'Edit Payment Plan' : 'Add Payment Plan'}</DialogTitle>
              <DialogDescription className="text-muted">
                One plan per vendor bill. Add as many installments as you need.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5 mt-4">
              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Description</Label>
                <Input value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-field h-12" placeholder="e.g., Venue booking"
                  required data-testid="payment-description-input" />
              </div>

              {/* Vendor picker */}
              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Vendor (optional)</Label>
                <Select
                  value={newVendor ? NEW_VENDOR_SENTINEL : (formData.vendor_id || '')}
                  onValueChange={handleVendorSelect}
                >
                  <SelectTrigger className="input-field h-12" data-testid="payment-vendor-select">
                    <SelectValue placeholder="No vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                    {vendors.length > 0 && <SelectSeparator />}
                    <SelectItem value={NEW_VENDOR_SENTINEL} className="text-terracotta" data-testid="payment-vendor-create-new">
                      + Create new vendor
                    </SelectItem>
                  </SelectContent>
                </Select>
                {newVendor && (
                  <div className="mt-3 p-4 border border-terracotta/30 bg-terracotta/5 rounded-sm space-y-3" data-testid="payment-new-vendor-form">
                    <p className="text-xs text-muted">
                      Quick vendor entry. You can add socials + events later on the Vendors tab.
                    </p>
                    <Input value={newVendor.name}
                      onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
                      className="input-field h-10" placeholder="Vendor name *" required
                      data-testid="payment-new-vendor-name" />
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={newVendor.category}
                        onValueChange={(c) => setNewVendor({ ...newVendor, category: c })}>
                        <SelectTrigger className="input-field h-10 text-xs" data-testid="payment-new-vendor-category">
                          <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input value={newVendor.phone}
                        onChange={(e) => setNewVendor({ ...newVendor, phone: e.target.value })}
                        className="input-field h-10" placeholder="Phone"
                        data-testid="payment-new-vendor-phone" />
                    </div>
                    <Input type="email" value={newVendor.email}
                      onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })}
                      className="input-field h-10" placeholder="Email"
                      data-testid="payment-new-vendor-email" />
                    <button type="button" onClick={() => setNewVendor(null)}
                      className="text-xs text-muted hover:text-charcoal transition-colors"
                      data-testid="payment-new-vendor-cancel">
                      ← Use an existing vendor instead
                    </button>
                  </div>
                )}
              </div>

              {/* Top-level "responsible family" — applies to all installments
                  unless a row overrides. Shown only when the wedding has a
                  family-budget split configured (legacy weddings won't see it). */}
              {(currentWedding?.partner1_family_budget || currentWedding?.partner2_family_budget || currentWedding?.joint_budget) ? (
                <div className="space-y-2">
                  <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">
                    Responsible family
                  </Label>
                  <Select
                    value={formData.responsible_family || ''}
                    onValueChange={(v) => setFormData({ ...formData, responsible_family: v === '__none__' ? '' : v })}
                  >
                    <SelectTrigger className="input-field h-12" data-testid="payment-responsible-family-select">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Unassigned</SelectItem>
                      {currentWedding?.partner1_name && (
                        <SelectItem value="partner1">{currentWedding.partner1_name}'s family</SelectItem>
                      )}
                      {currentWedding?.partner2_name && (
                        <SelectItem value="partner2">{currentWedding.partner2_name}'s family</SelectItem>
                      )}
                      <SelectItem value="joint">Joint</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted">
                    Who's expected to fund this. Each installment can override below.
                  </p>
                </div>
              ) : null}

              {/* Installments editor */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Installments</Label>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => applyPreset(2)}
                      className="text-[10px] uppercase tracking-widest text-muted hover:text-charcoal transition-colors"
                      data-testid="installment-preset-2">
                      2 payments
                    </button>
                    <span className="text-border">|</span>
                    <button type="button" onClick={() => applyPreset(3)}
                      className="text-[10px] uppercase tracking-widest text-muted hover:text-charcoal transition-colors"
                      data-testid="installment-preset-3">
                      3 payments
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  {formData.installments.map((inst, idx) => {
                    const showFamilyRow = Boolean(currentWedding?.partner1_family_budget || currentWedding?.partner2_family_budget || currentWedding?.joint_budget);
                    return (
                    <div key={inst._key} className="p-2 border border-border rounded-sm bg-page/30 space-y-2"
                      data-testid={`modal-installment-${idx}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-muted w-6 text-center">#{idx + 1}</span>
                      <Input
                        type="number" min="0" step="0.01"
                        value={inst.amount}
                        onChange={(e) => updateInstallment(idx, { amount: e.target.value })}
                        className="input-field h-10 font-mono flex-1" placeholder="Amount"
                        required data-testid={`modal-installment-amount-${idx}`}
                      />
                      <Input
                        type="date" value={inst.due_date}
                        onChange={(e) => updateInstallment(idx, { due_date: e.target.value })}
                        className="input-field h-10 flex-1"
                        required data-testid={`modal-installment-date-${idx}`}
                      />
                      <label className="flex items-center gap-1 shrink-0 cursor-pointer">
                        <Checkbox
                          checked={inst.paid}
                          onCheckedChange={(c) => updateInstallment(idx, {
                            paid: !!c,
                            paid_date: c ? new Date().toISOString().slice(0, 10) : null,
                          })}
                          data-testid={`modal-installment-paid-${idx}`}
                        />
                        <span className="text-[10px] uppercase tracking-widest text-muted">Paid</span>
                      </label>
                      {formData.installments.length > 1 && (
                        <button type="button" onClick={() => removeInstallment(idx)}
                          className="p-1 text-muted hover:text-terracotta transition-colors"
                          aria-label={`Remove installment ${idx + 1}`}
                          data-testid={`modal-installment-remove-${idx}`}>
                          <Xmark className="w-4 h-4" strokeWidth={1.75} />
                        </button>
                      )}
                    </div>
                    {showFamilyRow && (
                      <div className="grid grid-cols-2 gap-2 pl-8">
                        <Select
                          value={inst.responsible_family || ''}
                          onValueChange={(v) => updateInstallment(idx, { responsible_family: v === '__none__' ? '' : v })}
                        >
                          <SelectTrigger className="input-field h-9 text-xs" data-testid={`modal-installment-resp-${idx}`}>
                            <SelectValue placeholder="Resp: inherit" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">Inherit from plan</SelectItem>
                            {currentWedding?.partner1_name && (
                              <SelectItem value="partner1">Resp: {currentWedding.partner1_name}'s family</SelectItem>
                            )}
                            {currentWedding?.partner2_name && (
                              <SelectItem value="partner2">Resp: {currentWedding.partner2_name}'s family</SelectItem>
                            )}
                            <SelectItem value="joint">Resp: Joint</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select
                          value={inst.paid_by_family || ''}
                          onValueChange={(v) => updateInstallment(idx, { paid_by_family: v === '__none__' ? '' : v })}
                        >
                          <SelectTrigger className="input-field h-9 text-xs" data-testid={`modal-installment-paid-by-${idx}`}>
                            <SelectValue placeholder="Paid by: —" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">Paid by: —</SelectItem>
                            {currentWedding?.partner1_name && (
                              <SelectItem value="partner1">Paid by: {currentWedding.partner1_name}'s family</SelectItem>
                            )}
                            {currentWedding?.partner2_name && (
                              <SelectItem value="partner2">Paid by: {currentWedding.partner2_name}'s family</SelectItem>
                            )}
                            <SelectItem value="joint">Paid by: Joint</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    </div>
                  );})}
                </div>

                <div className="flex items-center justify-between pt-1">
                  <button type="button" onClick={addInstallment}
                    className="text-xs text-terracotta hover:text-terracotta-dark transition-colors flex items-center gap-1"
                    data-testid="add-installment-btn">
                    <Plus className="w-3 h-3" /> Add installment
                  </button>
                  <span className="font-mono text-xs text-muted">
                    Total: {formatCurrency(installmentsFormSum)}
                  </span>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Cancel</Button>
                <Button type="submit" className="btn-primary flex-1" data-testid="save-payment-btn">
                  {editingPayment ? 'Update plan' : 'Save plan'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
  );

  return bare ? content : <Layout>{content}</Layout>;
}
